import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import PaymentTable from "components/tables/PaymentTable.js";
import Header from "./../components/headers/HeaderLight"
import { Row, Col, Button, ButtonGroup } from "reactstrap";
import { ReactComponent as SvgDotPattern } from "images/dot-pattern.svg"
import { SectionHeading } from "components/misc/Headings.js";
import IllustrationSrc from "images/mobileTransfer.png";
import Faq from "components/faqs/SimpleWithSideImage.js";
import { ROUTE_PATHS } from "utils/routes";
import PropTypes from 'prop-types';
import { usePayments } from "hooks/usePayments.js";
import PaymentAccordion from "components/accordions/PaymentAccordion.js";
import Datetime from 'react-datetime';
import moment from 'moment';
import LoadingSpinner from "components/misc/LoadingSpinner.js";
import FilterByDates from "components/misc/FilterByDates.js";
import { AppContext } from "components/context/AppContext.js";

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(255,255,255,0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Container = tw.div`relative`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24 items-center`;
const ImageColumn = tw(Column)`md:w-6/12 flex-shrink-0 relative`;
const TextColumn = styled(Column)(props => [
    tw`md:w-6/12 mt-16 md:mt-0`,
    props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);
const Image = styled.img(props => [
    props.imageRounded && tw`rounded`,
    props.imageBorder && tw`border`,
    props.imageShadow && tw`shadow`,
]);

const DecoratorBlob = styled(SvgDotPattern)(props => [
    tw`w-20 h-20 absolute right-0 bottom-0 transform translate-x-1/2 translate-y-1/2 fill-current text-primary-500 -z-10`,
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;
const Heading = tw(
    SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;
const ButtonTw = tw(
    Button
)`px-8 py-3 font-bold rounded bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300 mt-12 inline-block tracking-wide text-center px-10 py-4 font-semibold tracking-normal`;
const Calendar = tw(Datetime)`text-blue-400`;

const faqs = [
    {
        question: "¿Cómo interpretar el estatus de mis cobros?",
        answer:
            "Cada cobro tiene un color que indica su estado: \ud83d\udfe1 Naranja = Pendiente de pago, \ud83d\udfe2 Verde = Pago confirmado y registrado por administración, \ud83d\udd34 Rojo = Pago rechazado o cancelado. Si tienes dudas sobre un cobro específico, contáctanos."
    },
    {
        question: "¿Cómo realizo un pago por transferencia bancaria?",
        answer:
            "Selecciona el o los cobros pendientes en la tabla, luego haz clic en 'Realizar Pago'. El sistema genera un código de referencia único de 6 dígitos. Realiza la transferencia desde tu app bancaria usando ese código en el campo Concepto/Referencia y envía el comprobante a administración."
    },
    {
        question: "¿En cuánto tiempo se verá reflejado mi pago?",
        answer:
            "Una vez que la administración recibe y verifica el comprobante de transferencia, tu pago se confirma en un plazo de 24 a 48 horas hábiles y el estatus cambia a Verde."
    },
    {
        question: "¿Puedo pagar varios cobros con una sola transferencia?",
        answer:
            "Sí. Selecciona todos los cobros que deseas cubrir marcando sus casillas en la tabla. El sistema acumula el total y genera un solo código de referencia para la transferencia completa."
    },
    {
        question: "¿Por qué aparece un cobro que ya pagué?",
        answer:
            "Si realizaste la transferencia pero no enviaste el comprobante a administración, el pago no puede ser confirmado. Envía la captura de pantalla de tu transferencia y el número de referencia al administrador para agilizar la confirmación."
    }
];

const PaymentPage = ({
    heading = (
        <>
            Tus pagos y adeudos de <span className="text-primary-500">mantenimiento.</span>
        </>
    ),
    description = "Revisa aquí todos tus cobros pendientes y el historial de pagos realizados. Selecciona uno o varios cargos para generar una referencia de pago por transferencia bancaria.",
    imageSrc = IllustrationSrc,
    imageRounded = true,
    imageBorder = false,
    imageShadow = false,
    imageCss = null,
    imageDecoratorBlob = false,
    imageDecoratorBlobCss = null,
    textOnLeft = true
}) => {
    const [title, setTitle] = useState("Cobros Pendientes");
    const [rSelected, setRSelected] = useState(1);
    const { payments, fetchPayments, loading } = usePayments();
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [selectedDateStart, setSelectedDateStart] = useState(null);
    const [selectedDateEnd, setSelectedDateEnd] = useState(null);
    const [toggledRows, setToggledRows] = useState([]);
    const navigate = useNavigate();
    const { state } = useContext(AppContext);
    const homeownerId = state.user?.homeowner?.homeownerId;

    // Format payments for PaymentTable
    const tableData = payments.map(p => ({
        id: p.paymentId,
        category: p.category,
        createdDate: p.createdDate,
        balance: `$${p.balance}`,
        referenceNumber: p.referenceNumber || "---",
        status: p.status
    }));

    React.useEffect(() => {
        const fetchData = async () => {
            await fetchPayments(homeownerId); // Example homeNumber
        };
        fetchData();
    }, []); // Only run once on mount

    // Filter by selected date
    React.useEffect(() => {
        let filtered = [];
        if (rSelected === 2) {
            filtered = tableData.filter(p => p.status === 1);
        } else {
            filtered = tableData.filter(p => p.status === 2 || p.status === 0);
        }

        if (selectedDateStart && selectedDateEnd) {
            filtered = filtered.filter(p => {
                const d = moment(p.createdDate, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day') && d.isSameOrBefore(selectedDateEnd, 'day');
            });
        } else if (selectedDateStart) {
            filtered = filtered.filter(p => {
                const d = moment(p.createdDate, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day');
            });
        } else if (selectedDateEnd) {
            filtered = filtered.filter(p => {
                const d = moment(p.createdDate, 'YYYY-MM-DD');
                return d.isSameOrBefore(selectedDateEnd, 'day');
            });
        }
        setFilteredTableData(filtered);
    }, [selectedDateStart, selectedDateEnd, rSelected, payments]);

    const handleTabChange = (isHistory) => {
        if (isHistory) {
            setRSelected(2);
            setTitle("Pagos Realizados");
        } else {
            setRSelected(1);
            setTitle("Cobros Pendientes");
        }
    };

    return (
        <>
            {loading && (
                <SpinnerOverlay>
                    <LoadingSpinner text="Cargando tus pagos…" />
                </SpinnerOverlay>
            )}
            {!loading && (
                <AnimationRevealPage>
                    <Header />
                    {/* Title */}
                    <Container>
                        <TwoColumn>
                            <ImageColumn>
                                <Image css={imageCss} src={imageSrc} imageBorder={imageBorder} imageShadow={imageShadow} imageRounded={imageRounded} />
                                {imageDecoratorBlob && <DecoratorBlob css={imageDecoratorBlobCss} />}
                            </ImageColumn>
                            <TextColumn textOnLeft={textOnLeft}>
                                <TextContent>
                                    {/* <Subheading>{subheading}</Subheading> */}
                                    <Heading>{heading}</Heading>
                                    <Description>{description}</Description>
                                </TextContent>
                            </TextColumn>
                        </TwoColumn>
                    </Container>
                    <Row>
                        <Col lg="8" style={{ position: 'relative', display: 'inline-block' }}>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <ButtonTw
                                    className="my-2"
                                    size="lg"
                                    style={{ position: 'relative' }}
                                    disabled={toggledRows.length === 0}
                                    onClick={() => {
                                        // Filter payments by toggledRows
                                        const selectedPayments = tableData.filter(p => toggledRows.includes(`row-${p.id}`));
                                        navigate(ROUTE_PATHS.NEWPAYMENT, { state: { selectedPayments } });
                                    }}
                                >
                                    Ver Carrito
                                    {toggledRows.length > 0 && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: '-8px',
                                                right: '-8px',
                                                minWidth: '22px',
                                                height: '22px',
                                                background: '#3B82F6',
                                                color: 'white',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.85rem',
                                                fontWeight: 'bold',
                                                boxShadow: '0 2px 8px rgba(59,130,246,0.15)',
                                                border: '2px solid white',
                                                zIndex: 10
                                            }}
                                        >
                                            {toggledRows.length}
                                        </span>
                                    )}
                                </ButtonTw>
                            </div>
                        </Col>
                        <Col lg="4" style={{ textAlign: "right" }}>
                            <ButtonGroup className="my-2" size="lg">
                                <Button outline active={rSelected === 1} onClick={() => handleTabChange(false)}>
                                    Pendientes
                                </Button>
                                <Button outline active={rSelected === 2} onClick={() => handleTabChange(true)}>
                                    Pagados
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg="4">
                            <FilterByDates
                                selectedDateStart={selectedDateStart}
                                setSelectedDateStart={setSelectedDateStart}
                                selectedDateEnd={selectedDateEnd}
                                setSelectedDateEnd={setSelectedDateEnd}
                            />
                        </Col>
                    </Row>

                    {/* Desktop Table - Only show PaymentTable on md and up */}
                    <div className="hidden md:block w-full">
                        <PaymentTable title={title} tableData={filteredTableData} toggledRows={toggledRows} setToggledRows={setToggledRows} />
                    </div>

                    {/* Mobile Accordions - hidden on md and up */}
                    <div className="md:hidden px-2 py-4">
                        <PaymentAccordion tableData={filteredTableData} toggledRows={toggledRows} setToggledRows={setToggledRows} />
                    </div>

                    <Faq faqs={faqs} />
                    <Footer />
                </AnimationRevealPage>
            )}
        </>
    )
};

PaymentPage.propTypes = {
    heading: PropTypes.node,
    description: PropTypes.string,
    imageSrc: PropTypes.string,
    imageRounded: PropTypes.bool,
    imageBorder: PropTypes.bool,
    imageShadow: PropTypes.bool,
    imageCss: PropTypes.any,
    imageDecoratorBlob: PropTypes.bool,
    imageDecoratorBlobCss: PropTypes.any,
    textOnLeft: PropTypes.bool
};

export default PaymentPage;
